(() => {
  const SELECTOR = '.rrr-desktop-header';

  class RRRDesktopHeader {
    constructor(root) {
      this.root = root;
      this.toggle = root.querySelector('[data-rrr-drawer-toggle]');
      this.closeButton = root.querySelector('[data-rrr-drawer-close]');
      this.drawer = root.querySelector('[data-rrr-drawer]');
      this.overlay = root.querySelector('[data-rrr-drawer-overlay]');
      this.tabs = [...root.querySelectorAll('[data-rrr-tab]')];
      this.audienceItems = [...root.querySelectorAll('[data-audience]')];
      this.activeAudience = 'men';
      this.lastFocused = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundScroll = this.onScroll.bind(this);
      this.boundResize = this.onResize.bind(this);

      if (!this.toggle || !this.drawer || !this.overlay) return;

      this.toggle.addEventListener('click', () => this.toggleDrawer());
      this.closeButton?.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setAudience(tab.dataset.rrrTab));
      });

      this.setAudience(this.activeAudience);
      window.addEventListener('scroll', this.boundScroll, { passive: true });
      window.addEventListener('resize', this.boundResize, { passive: true });
      this.onScroll();
    }

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    toggleDrawer() {
      this.isOpen() ? this.close() : this.open();
    }

    open() {
      this.lastFocused = document.activeElement;
      this.overlay.hidden = false;

      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.overlay.classList.add('is-visible');
      });

      this.toggle.setAttribute('aria-expanded', 'true');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rrr-desktop-drawer-open');
      document.addEventListener('keydown', this.boundKeydown);

      window.setTimeout(() => this.closeButton?.focus({ preventScroll: true }), 30);
    }

    close() {
      if (!this.isOpen()) return;

      this.drawer.classList.remove('is-open');
      this.overlay.classList.remove('is-visible');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rrr-desktop-drawer-open');
      document.removeEventListener('keydown', this.boundKeydown);

      const duration = parseFloat(
        getComputedStyle(this.root).getPropertyValue('--rrr-animation-speed')
      ) || 260;

      window.setTimeout(() => {
        this.overlay.hidden = true;
      }, duration);

      this.lastFocused?.focus?.({ preventScroll: true });
    }

    setAudience(audience) {
      this.activeAudience = audience;

      this.tabs.forEach((tab) => {
        const active = tab.dataset.rrrTab === audience;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });

      this.audienceItems.forEach((item) => {
        const value = item.dataset.audience;
        item.hidden = !(value === audience || value === 'both');
      });
    }

    onScroll() {
      this.root.classList.toggle('is-scrolled', window.scrollY > 8);
    }

    onResize() {
      if (window.innerWidth < 990 && this.isOpen()) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.isOpen()) return;

      const focusable = [
        ...this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), summary, input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.rrrInitialized === 'true') return;
      root.dataset.rrrInitialized = 'true';
      new RRRDesktopHeader(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
